import type { CefrLevel, Plan } from '@tutlo/contracts'

/**
 * Deterministyczny plan z odpowiedzi flow `default` (ekran „Twój plan” v6, Figma `278:561`).
 * Tabele zamiast LLM: agent (M4) może je później nadpisać, ale wynik musi być natychmiastowy (nigdy spinner).
 */

const LEVELS: Record<string, { from: CefrLevel; to: CefrLevel }> = {
  a0: { from: 'A0', to: 'A1' },
  a2: { from: 'A2', to: 'B1' },
  b1: { from: 'B1', to: 'B2' },
  b2: { from: 'B2', to: 'C1' },
}

type Tempo = {
  perWeek: Plan['tempoPerWeek']
  label: string
  months: number
  fasterMonths: number
}
const TEMPOS: Record<string, Tempo> = {
  daily15: { perWeek: 5, label: 'codziennie', months: 2, fasterMonths: 1.5 },
  '2x30': { perWeek: 2, label: '2×/tydz', months: 4, fasterMonths: 2.5 },
  weekend60: { perWeek: 1, label: '1×/tydz', months: 6, fasterMonths: 4 },
}
const DEFAULT_TEMPO = TEMPOS['2x30'] as Tempo

type Goal = { label: string; milestone: string; ctxId: string; ctx: Record<string, string> }
const GOALS: Record<string, Goal> = {
  work: {
    label: 'praca',
    milestone: 'Pierwsze swobodne zdania w pracy',
    ctxId: 'work_ctx',
    ctx: {
      meetings: 'spotkania i calle',
      mail: 'maile',
      presentations: 'prezentacje',
      recruitment: 'rekrutacja',
    },
  },
  exam: {
    label: 'egzamin',
    milestone: 'Pierwszy próbny test bez stresu',
    ctxId: 'exam_ctx',
    ctx: { matura: 'matura', ielts: 'IELTS/Cambridge', certificate: 'certyfikat', unknown: '' },
  },
  travel: {
    label: 'podróże',
    milestone: 'Pierwsza rozmowa bez tłumacza',
    ctxId: 'travel_ctx',
    ctx: {
      daily: 'codzienne sytuacje',
      people: 'rozmowy z ludźmi',
      formal: 'formalności',
      confidence: 'pewność siebie',
    },
  },
  talk: {
    label: 'codzienna komunikacja',
    milestone: 'Pierwsza swobodna pogawędka',
    ctxId: 'talk_ctx',
    ctx: { life: 'życie codzienne', work: 'praca', travel: 'podróże i kultura', fun: 'rozrywka' },
  },
}

export const MILESTONE_AFTER_LESSONS = 4

export function buildPlan(answers: Readonly<Record<string, string>>): Plan {
  const goal = GOALS[answers.goal ?? '']
  if (!goal) throw new Error(`buildPlan: brak lub nieznany goal (${answers.goal})`)
  const level = LEVELS[answers.level ?? '']
  if (!level) throw new Error(`buildPlan: brak lub nieznany level (${answers.level})`)
  const tempo = TEMPOS[answers.time ?? ''] ?? DEFAULT_TEMPO
  const focusLabel = goal.ctx[answers[goal.ctxId] ?? ''] ?? ''
  return {
    levelFrom: level.from,
    levelTo: level.to,
    tempoPerWeek: tempo.perWeek,
    tempoLabel: tempo.label,
    monthsToGoal: tempo.months,
    monthsFaster: tempo.fasterMonths,
    goalLabel: goal.label,
    focusLabel,
    milestone: goal.milestone,
    milestoneAfterLessons: MILESTONE_AFTER_LESSONS,
    chart: {
      from: level.from,
      to: level.to,
      months: tempo.months,
      fasterMonths: tempo.fasterMonths,
    },
  }
}
